import { SOB_QUARTER_KEYS } from '../model/constants'
import type { SobInputKey, SobInputStateByKey, SobQuarterKey, SobTableEntry } from '../model/types'

import {
  createSobInputState,
  flattenSobTableEntries,
  recalculateCategoryOverallInputs,
} from './sobInputState'

const SOB_INPUT_KEYS = [
  'prevActuals',
  'currentActuals',
  'currentPlan',
] as const satisfies readonly SobInputKey[]

/** Quarters whose subcategory/L3 values differ from their baseline and await an overall recalculation */
export type SobPendingRecalcQuarters = Partial<Record<SobInputKey, readonly SobQuarterKey[]>>

/** Overall values as they were before the last recalculation, kept for cancel */
export type SobOverallRevertValues = Partial<
  Record<SobInputKey, Partial<Record<SobQuarterKey, number | null>>>
>

/** Subcategory/L3 values captured at recalculation time; quarters equal to their baseline are not pending */
export type SobEntryBaselineValues = Partial<
  Record<SobInputKey, Partial<Record<SobQuarterKey, number | null>>>
>

export type SobCategoryRecalcState = {
  revertValues: SobOverallRevertValues
  baselineByEntryKey: Record<string, SobEntryBaselineValues>
}

export type SobRecalcStateByCategory = Record<string, SobCategoryRecalcState>

const EMPTY_CATEGORY_RECALC_STATE: SobCategoryRecalcState = {
  revertValues: {},
  baselineByEntryKey: {},
}

const getCategorySourceEntries = (entries: SobTableEntry[], category: string) =>
  flattenSobTableEntries(entries).filter((entry) => entry.category === category && !entry.isOverall)

const findCategoryOverallEntry = (entries: SobTableEntry[], category: string) =>
  flattenSobTableEntries(entries).find((entry) => entry.category === category && entry.isOverall)

const getEntryBaselineValue = (
  entry: SobTableEntry,
  inputKey: SobInputKey,
  quarterKey: SobQuarterKey,
  state?: SobCategoryRecalcState
): number | null => {
  const baselineQuarterValues = state?.baselineByEntryKey[entry.key]?.[inputKey]

  if (baselineQuarterValues && quarterKey in baselineQuarterValues) {
    return baselineQuarterValues[quarterKey] ?? null
  }

  return entry.model.sob[inputKey][quarterKey]
}

/**
 * Pending quarters are derived, not accumulated: a quarter stops being pending as soon as
 * every subcategory/L3 value returns to its baseline (e.g. the user reverts an edit)
 */
export const getPendingRecalcQuarters = ({
  entries,
  inputsByKey,
  category,
  state,
}: {
  entries: SobTableEntry[]
  inputsByKey: SobInputStateByKey
  category: string
  state?: SobCategoryRecalcState
}): SobPendingRecalcQuarters => {
  const sourceEntries = getCategorySourceEntries(entries, category)
  const pendingQuarters: Partial<Record<SobInputKey, SobQuarterKey[]>> = {}

  SOB_INPUT_KEYS.forEach((inputKey) => {
    const quarterKeys = SOB_QUARTER_KEYS.filter((quarterKey) =>
      sourceEntries.some((entry) => {
        const entryInputs = inputsByKey[entry.key] ?? createSobInputState(entry.model)

        return (
          entryInputs[inputKey][quarterKey] !==
          getEntryBaselineValue(entry, inputKey, quarterKey, state)
        )
      })
    )

    if (quarterKeys.length) {
      pendingQuarters[inputKey] = quarterKeys
    }
  })

  return pendingQuarters
}

export const hasPendingRecalcQuarters = (pendingQuarters: SobPendingRecalcQuarters): boolean =>
  SOB_INPUT_KEYS.some((inputKey) => !!pendingQuarters[inputKey]?.length)

export const hasOverallRevertValues = (state?: SobCategoryRecalcState): boolean =>
  !!state &&
  SOB_INPUT_KEYS.some((inputKey) => {
    const revertQuarterValues = state.revertValues[inputKey]

    return !!revertQuarterValues && Object.keys(revertQuarterValues).length > 0
  })

/** A manual overall edit wins over cancel: the edited quarters are no longer revertable */
export const clearOverallRevertQuarters = (
  stateByCategory: SobRecalcStateByCategory,
  category: string,
  inputKey: SobInputKey,
  quarterKeys: readonly SobQuarterKey[]
): SobRecalcStateByCategory => {
  const categoryState = stateByCategory[category]
  const revertQuarterValues = categoryState?.revertValues[inputKey]

  if (!categoryState || !revertQuarterValues) {
    return stateByCategory
  }

  const clearedQuarters = quarterKeys.filter((quarterKey) => quarterKey in revertQuarterValues)

  if (!clearedQuarters.length) {
    return stateByCategory
  }

  const nextRevertQuarterValues = { ...revertQuarterValues }

  clearedQuarters.forEach((quarterKey) => {
    delete nextRevertQuarterValues[quarterKey]
  })

  const nextRevertValues = { ...categoryState.revertValues }

  if (Object.keys(nextRevertQuarterValues).length) {
    nextRevertValues[inputKey] = nextRevertQuarterValues
  } else {
    delete nextRevertValues[inputKey]
  }

  return {
    ...stateByCategory,
    [category]: {
      ...categoryState,
      revertValues: nextRevertValues,
    },
  }
}

export const applyCategoryOverallRecalc = ({
  entries,
  inputsByKey,
  stateByCategory,
  category,
}: {
  entries: SobTableEntry[]
  inputsByKey: SobInputStateByKey
  stateByCategory: SobRecalcStateByCategory
  category: string
}): { inputsByKey: SobInputStateByKey; stateByCategory: SobRecalcStateByCategory } => {
  const categoryState = stateByCategory[category] ?? EMPTY_CATEGORY_RECALC_STATE
  const overallEntry = findCategoryOverallEntry(entries, category)
  const pendingQuarters = getPendingRecalcQuarters({
    entries,
    inputsByKey,
    category,
    state: categoryState,
  })

  if (!hasPendingRecalcQuarters(pendingQuarters) || !overallEntry) {
    return { inputsByKey, stateByCategory }
  }

  const sourceEntries = getCategorySourceEntries(entries, category)
  let nextInputsByKey = inputsByKey
  const nextRevertValues: SobOverallRevertValues = { ...categoryState.revertValues }
  const nextBaselineByEntryKey: Record<string, SobEntryBaselineValues> = {
    ...categoryState.baselineByEntryKey,
  }

  SOB_INPUT_KEYS.forEach((inputKey) => {
    const quarterKeys = pendingQuarters[inputKey]

    if (!quarterKeys?.length) {
      return
    }

    const beforeQuarterValues = (nextInputsByKey[overallEntry.key] ??
      createSobInputState(overallEntry.model))[inputKey]

    nextInputsByKey = recalculateCategoryOverallInputs({
      entries,
      inputsByKey: nextInputsByKey,
      inputKey,
      category,
      quarterKeys,
    })

    const afterQuarterValues = (nextInputsByKey[overallEntry.key] ??
      createSobInputState(overallEntry.model))[inputKey]
    const revertQuarterValues = { ...nextRevertValues[inputKey] }
    let hasNewRevertValues = false

    quarterKeys.forEach((quarterKey) => {
      // Keep the earliest snapshot so repeated recalcs still cancel back to the pre-recalc values
      if (
        beforeQuarterValues[quarterKey] !== afterQuarterValues[quarterKey] &&
        !(quarterKey in revertQuarterValues)
      ) {
        revertQuarterValues[quarterKey] = beforeQuarterValues[quarterKey]
        hasNewRevertValues = true
      }
    })

    if (hasNewRevertValues) {
      nextRevertValues[inputKey] = revertQuarterValues
    }

    // The recalculated values become the new baseline, so these quarters stop being pending
    sourceEntries.forEach((entry) => {
      const entryInputs = nextInputsByKey[entry.key] ?? createSobInputState(entry.model)
      const entryBaseline = { ...nextBaselineByEntryKey[entry.key] }
      const baselineQuarterValues = { ...entryBaseline[inputKey] }

      quarterKeys.forEach((quarterKey) => {
        baselineQuarterValues[quarterKey] = entryInputs[inputKey][quarterKey]
      })

      entryBaseline[inputKey] = baselineQuarterValues
      nextBaselineByEntryKey[entry.key] = entryBaseline
    })
  })

  return {
    inputsByKey: nextInputsByKey,
    stateByCategory: {
      ...stateByCategory,
      [category]: {
        revertValues: nextRevertValues,
        baselineByEntryKey: nextBaselineByEntryKey,
      },
    },
  }
}

/** Restores every subcategory/L3 value of the category to its baseline, discarding the pending edits */
export const resetPendingRecalcChanges = ({
  entries,
  inputsByKey,
  category,
  state,
}: {
  entries: SobTableEntry[]
  inputsByKey: SobInputStateByKey
  category: string
  state?: SobCategoryRecalcState
}): SobInputStateByKey => {
  const sourceEntries = getCategorySourceEntries(entries, category)
  let nextInputsByKey = inputsByKey

  sourceEntries.forEach((entry) => {
    const entryInputs = inputsByKey[entry.key] ?? createSobInputState(entry.model)
    let nextEntryInputs = entryInputs

    SOB_INPUT_KEYS.forEach((inputKey) => {
      SOB_QUARTER_KEYS.forEach((quarterKey) => {
        const baselineValue = getEntryBaselineValue(entry, inputKey, quarterKey, state)

        if (nextEntryInputs[inputKey][quarterKey] !== baselineValue) {
          nextEntryInputs = {
            ...nextEntryInputs,
            [inputKey]: {
              ...nextEntryInputs[inputKey],
              [quarterKey]: baselineValue,
            },
          }
        }
      })
    })

    if (nextEntryInputs !== entryInputs) {
      nextInputsByKey = {
        ...nextInputsByKey,
        [entry.key]: nextEntryInputs,
      }
    }
  })

  return nextInputsByKey
}

export const revertCategoryOverallRecalc = ({
  entries,
  inputsByKey,
  stateByCategory,
  category,
}: {
  entries: SobTableEntry[]
  inputsByKey: SobInputStateByKey
  stateByCategory: SobRecalcStateByCategory
  category: string
}): { inputsByKey: SobInputStateByKey; stateByCategory: SobRecalcStateByCategory } => {
  const categoryState = stateByCategory[category]
  const overallEntry = findCategoryOverallEntry(entries, category)

  if (!categoryState || !hasOverallRevertValues(categoryState) || !overallEntry) {
    return { inputsByKey, stateByCategory }
  }

  const overallInputs = inputsByKey[overallEntry.key] ?? createSobInputState(overallEntry.model)
  const nextOverallInputs = { ...overallInputs }

  SOB_INPUT_KEYS.forEach((inputKey) => {
    const revertQuarterValues = categoryState.revertValues[inputKey]

    if (!revertQuarterValues) {
      return
    }

    const revertedQuarters = SOB_QUARTER_KEYS.filter(
      (quarterKey) => quarterKey in revertQuarterValues
    )

    if (!revertedQuarters.length) {
      return
    }

    const nextQuarterValues = { ...nextOverallInputs[inputKey] }

    revertedQuarters.forEach((quarterKey) => {
      nextQuarterValues[quarterKey] = revertQuarterValues[quarterKey] ?? null
    })

    nextOverallInputs[inputKey] = nextQuarterValues
  })

  // Dropping the baselines makes the subcategory edits pending again, as before the recalculation
  return {
    inputsByKey: {
      ...inputsByKey,
      [overallEntry.key]: nextOverallInputs,
    },
    stateByCategory: {
      ...stateByCategory,
      [category]: {
        revertValues: {},
        baselineByEntryKey: {},
      },
    },
  }
}
